import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { SeletorDeItens } from './SeletorDeItens';
import { formatNumber } from '../../utils/formatters';
import { caminhoDaArea, caminhoSuave, topoDoEixo, type Ponto } from '../../utils/graficoDeMontanha';
import { usosPorExtenso } from '../../utils/usageStats';

export interface PontoDoGrafico {
  id: string;
  rotulo: string;
  detalhe?: string | null;
  valor: number;
}

const ALTURA = 200;
const MARGEM = { topo: 16, direita: 16, base: 8, esquerda: 40 };
// Abaixo disso cada nome não cabe embaixo do ponto: o eixo mostra a posição (1º, 2º...) e o nome
// fica no balão ao passar o mouse.
const ESPACO_MINIMO_PARA_NOME = 64;
// Quantos itens aparecem antes de a pessoa mexer no "Comparar".
const ITENS_POR_PADRAO = 10;

/**
 * Gráfico de área ("montanha") do dashboard, no lugar das barras de "Quem mais usa" e "Credenciais
 * mais usadas". Os mesmos dados, na mesma ordem (do mais usado ao menos usado): a curva se desenha da
 * esquerda para a direita ao aparecer e ao trocar de período. Ao passar o mouse (ou focar pelo
 * teclado), uma linha vertical encaixa no ponto mais perto e o balão mostra nome e usos. Com
 * `onSelecionar`, clicar num ponto vira filtro (clicar de novo desmarca), como era nas barras.
 * O botão "Comparar" escolhe quais itens aparecem, para comparar só os que interessam.
 */
export function GraficoDeMontanha({
  titulo,
  pontos: todos,
  nomeDoItem,
  vazio,
  selecionada,
  onSelecionar,
  sufixo,
}: {
  titulo: string;
  pontos: PontoDoGrafico[];
  /** "pessoas", "credenciais": texto do seletor "Comparar". */
  nomeDoItem: string;
  vazio: string;
  selecionada?: string | null;
  onSelecionar?: (id: string | null) => void;
  sufixo?: string | null;
}) {
  const caixaRef = useRef<HTMLDivElement>(null);
  const [largura, setLargura] = useState(0);
  const [montado, setMontado] = useState(false);
  const [ativo, setAtivo] = useState<number | null>(null);
  // Itens marcados no "Comparar". null = ainda não mexeu: aparecem os 10 mais usados (o servidor manda
  // todos com uso no período). Depois de mexer, a escolha fica guardada pelo id ao trocar o período.
  const [escolhidos, setEscolhidos] = useState<Set<string> | null>(null);
  const visiveis = useMemo(
    () => escolhidos ?? new Set(todos.slice(0, ITENS_POR_PADRAO).map((p) => p.id)),
    [escolhidos, todos]
  );
  const pontos = useMemo(() => todos.filter((p) => visiveis.has(p.id)), [todos, visiveis]);
  const chaveDosPontos = pontos.map((p) => `${p.id}:${p.valor}`).join('|');
  // useId devolve ":r1:"; dentro de url(#...) só letras e números são seguros.
  const idDoGradiente = `montanha-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  useEffect(() => {
    const caixa = caixaRef.current;
    if (!caixa) return;
    const medir = () => setLargura(caixa.clientWidth);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(caixa);
    return () => observador.disconnect();
  }, [pontos.length > 0]);

  // Redesenha a curva quando os dados mudam (período, pessoa escolhida ou itens comparados).
  useEffect(() => {
    setMontado(false);
    setAtivo(null);
    const quadro = requestAnimationFrame(() => setMontado(true));
    return () => cancelAnimationFrame(quadro);
  }, [chaveDosPontos]);

  const clicavel = !!onSelecionar;
  const larguraDoPlot = Math.max(0, largura - MARGEM.esquerda - MARGEM.direita);
  // Os pontos das pontas ficam um pouco para dentro, para o nome embaixo deles não sair do cartão.
  const recuo = Math.min(32, larguraDoPlot / 6);
  const alturaDoPlot = ALTURA - MARGEM.topo - MARGEM.base;
  const base = MARGEM.topo + alturaDoPlot;
  const topo = topoDoEixo(Math.max(0, ...pontos.map((p) => p.valor)));
  const passo = pontos.length > 1 ? (larguraDoPlot - 2 * recuo) / (pontos.length - 1) : larguraDoPlot;
  const xDe = (i: number) => (pontos.length > 1 ? MARGEM.esquerda + recuo + i * passo : MARGEM.esquerda + larguraDoPlot / 2);
  const yDe = (valor: number) => MARGEM.topo + alturaDoPlot * (1 - valor / topo);
  const coordenadas: Ponto[] = pontos.map((p, i) => ({ x: xDe(i), y: yDe(p.valor) }));
  // Um ponto só: a linha vira um platô de ponta a ponta, para a área não sumir.
  const desenho: Ponto[] = coordenadas.length === 1
    ? [{ x: MARGEM.esquerda, y: coordenadas[0].y }, { x: MARGEM.esquerda + larguraDoPlot, y: coordenadas[0].y }]
    : coordenadas;
  const larguraDoAlvo = pontos.length > 1 ? passo : larguraDoPlot;
  const mostrarNomes = pontos.length > 0 && (pontos.length === 1 || passo >= ESPACO_MINIMO_PARA_NOME);
  const ticks = [topo, topo / 2, 0];

  const escolher = (i: number) => {
    if (!onSelecionar) return;
    const id = pontos[i].id;
    onSelecionar(selecionada === id ? null : id);
  };

  const pontoDoBalao = ativo !== null ? pontos[ativo] : null;
  const xDoBalao = ativo !== null ? Math.min(Math.max(coordenadas[ativo].x, 90), Math.max(90, largura - 90)) : 0;

  return (
    <div className="glass" style={{ borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)', boxShadow: 'var(--shadow-md)', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        <h4 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)', overflowWrap: 'anywhere', minWidth: 0, paddingTop: 6 }}>
          {titulo}
          {sufixo && <span style={{ color: 'var(--color-primary-400)', fontWeight: 'normal' }}> · {sufixo}</span>}
        </h4>
        {todos.length > 1 && <SeletorDeItens itens={todos} visiveis={visiveis} aoMudar={setEscolhidos} nomeDoItem={nomeDoItem} />}
      </div>

      {todos.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>{vazio}</p>
      ) : pontos.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', minHeight: ALTURA, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          Marque em "Comparar" pelo menos uma das {nomeDoItem}.
        </p>
      ) : (
        <div ref={caixaRef} style={{ position: 'relative', width: '100%' }} onPointerLeave={() => setAtivo(null)}>
          {largura > 0 && (
            <svg width={largura} height={ALTURA} role="img" aria-label={`${titulo}: gráfico de área`} style={{ display: 'block', overflow: 'visible' }}>
              <defs>
                <linearGradient id={idDoGradiente} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-primary-500)" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="var(--color-primary-500)" stopOpacity={0} />
                </linearGradient>
                {/* A cortina que revela a curva da esquerda para a direita */}
                <clipPath id={`${idDoGradiente}-cortina`}>
                  <rect
                    x={0}
                    y={0}
                    width={largura}
                    height={ALTURA + 8}
                    style={{
                      transform: montado ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: '0 0',
                      transition: montado ? 'transform 1100ms cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
                    }}
                  />
                </clipPath>
              </defs>

              {/* Grade horizontal discreta e valores do eixo Y */}
              {ticks.map((t) => (
                <g key={t}>
                  <line x1={MARGEM.esquerda} x2={MARGEM.esquerda + larguraDoPlot} y1={yDe(t)} y2={yDe(t)} stroke="var(--color-border-default)" strokeWidth={1} />
                  <text x={MARGEM.esquerda - 8} y={yDe(t)} dy="0.32em" textAnchor="end" fontSize={12} fill="var(--color-text-muted)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(t)}
                  </text>
                </g>
              ))}

              <g clipPath={`url(#${idDoGradiente}-cortina)`}>
                <path d={caminhoDaArea(desenho, base)} fill={`url(#${idDoGradiente})`} />
                <path d={caminhoSuave(desenho)} fill="none" stroke="var(--color-primary-400)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </g>

              {/* Linha vertical que acompanha o mouse */}
              {ativo !== null && (
                <line x1={coordenadas[ativo].x} x2={coordenadas[ativo].x} y1={MARGEM.topo} y2={base} stroke="var(--color-border-strong)" strokeWidth={1} />
              )}

              {/* Pontos: aparecem em sequência, depois da curva */}
              {coordenadas.map((c, i) => {
                const escolhida = selecionada === pontos[i].id;
                const destaque = escolhida || ativo === i;
                return (
                  <circle
                    key={pontos[i].id}
                    cx={c.x}
                    cy={c.y}
                    r={destaque ? 6 : 4}
                    fill={escolhida ? 'var(--color-primary-300)' : 'var(--color-primary-400)'}
                    stroke="var(--color-bg-surface)"
                    strokeWidth={2}
                    style={{
                      opacity: montado ? (selecionada && !escolhida ? 0.45 : 1) : 0,
                      transition: `opacity 300ms ease ${montado ? 500 + i * 60 : 0}ms, r 150ms ease`,
                    }}
                  />
                );
              })}

              {/* Alvos invisíveis, uma faixa por ponto (mouse, toque e teclado) */}
              {coordenadas.map((c, i) => {
                const p = pontos[i];
                const escolhida = selecionada === p.id;
                const descricao = `${p.rotulo}${p.detalhe ? `, ${p.detalhe}` : ''}: ${usosPorExtenso(p.valor)}`;
                return (
                  <rect
                    key={`alvo-${p.id}`}
                    x={c.x - larguraDoAlvo / 2}
                    y={MARGEM.topo}
                    width={Math.max(larguraDoAlvo, 24)}
                    height={alturaDoPlot}
                    fill="transparent"
                    tabIndex={0}
                    role={clicavel ? 'button' : undefined}
                    aria-pressed={clicavel ? escolhida : undefined}
                    aria-label={clicavel ? `${descricao}. ${escolhida ? 'Clique para ver todos' : 'Clique para filtrar'}` : descricao}
                    style={{ cursor: clicavel ? 'pointer' : 'default', outline: 'none' }}
                    onPointerEnter={() => setAtivo(i)}
                    onPointerMove={() => setAtivo(i)}
                    onFocus={() => setAtivo(i)}
                    onBlur={() => setAtivo(null)}
                    onClick={() => escolher(i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        escolher(i);
                      }
                    }}
                  />
                );
              })}
            </svg>
          )}

          {/* Eixo X: nome embaixo de cada ponto quando cabe; senão, a posição no ranking */}
          <div aria-hidden="true" style={{ position: 'relative', height: 20, marginTop: 6 }}>
            {coordenadas.map((c, i) => {
              const larguraDoRotulo = mostrarNomes ? Math.min(passo || larguraDoPlot, 120) - 4 : 32;
              return (
                <span
                  key={`x-${pontos[i].id}`}
                  style={{
                    position: 'absolute',
                    left: c.x - larguraDoRotulo / 2,
                    width: larguraDoRotulo,
                    textAlign: 'center',
                    fontSize: 'var(--text-xs)',
                    color: selecionada === pontos[i].id ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                    fontWeight: selecionada === pontos[i].id ? 'var(--font-medium)' : 'normal',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {mostrarNomes ? pontos[i].rotulo : `${i + 1}º`}
                </span>
              );
            })}
          </div>

          {/* Balão: o valor em destaque, o nome embaixo */}
          {pontoDoBalao && ativo !== null && (
            <div
              role="status"
              style={{
                position: 'absolute',
                left: xDoBalao,
                // Ponto perto do topo: o balão vai para baixo dele, para não cobrir o título.
                top: coordenadas[ativo].y < 80 ? coordenadas[ativo].y + 14 : coordenadas[ativo].y - 12,
                transform: coordenadas[ativo].y < 80 ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
                maxWidth: 180,
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--color-bg-elevated)',
                border: '1px solid var(--color-border-strong)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-lg)',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {usosPorExtenso(pontoDoBalao.valor)}
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>
                {pontoDoBalao.rotulo}
                {pontoDoBalao.detalhe && <> · {pontoDoBalao.detalhe}</>}
              </div>
              {clicavel && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                  {selecionada === pontoDoBalao.id ? 'Clique para ver todos' : 'Clique para filtrar'}
                </div>
              )}
            </div>
          )}

          {/* Os mesmos números em tabela, para leitores de tela */}
          <table className="sr-only">
            <caption>{titulo}</caption>
            <thead><tr><th>Nome</th><th>Usos</th></tr></thead>
            <tbody>
              {pontos.map((p) => (
                <tr key={`t-${p.id}`}><td>{p.rotulo}{p.detalhe ? ` · ${p.detalhe}` : ''}</td><td>{p.valor}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
